import type { Dispatch } from 'redux'
import { storageHttpClient, ws } from 'shared/store/ws'
import endpoints from 'shared/store/endpoints'
import { fetchAppSettings } from 'shared/store/application'
import {
    catalogType,
    ecommerceType,
    errors,
    orderPickUpType
} from 'shared/utils'
import type { CacCatalog, DeviceCheck, OperationDevice } from 'shared/types'
import type { RootState } from 'shared/store/reducers'
import type {
    ActionOperationRepeatDriveCode,
    ActionOperationRepeatSwapCode
} from 'shared/store/custom-operation/generic/actions'
import { getMenuById, getProductById } from './itemSelectors'
import { fetchPageData } from 'shared/store/actions'

const { DELIVERY_IN_APP } = ecommerceType
const {
    TAKEAWAY,
    ONSITE,
    TABLESERVICE,
    DRIVE,
    PARKING,
    TABLE_ORDER
} = orderPickUpType

type GetState = () => RootState

export type ActionCatalogFetched = {
    type: 'ECOMM_CATALOG_FETCHED',
    catalog: CacCatalog
}

const actionCatalogFetched = (catalog: CacCatalog): ActionCatalogFetched => ({
    type: 'ECOMM_CATALOG_FETCHED',
    catalog
})

export type ActionAllergensSelected = {
    type: 'ECOMM_ALLERGENS_SELECTED',
    selectedAllergens: Array<string>
}

const selectAllergens = (
    selectedAllergens: Array<string>
): ActionAllergensSelected => ({
    type: 'ECOMM_ALLERGENS_SELECTED',
    selectedAllergens
})

export type ActionWithoutBaconToggled = {
    type: 'ECOMM_WITHOUT_BACON_TOGGLED'
}

const toggleWithoutBacon = (): ActionWithoutBaconToggled => ({
    type: 'ECOMM_WITHOUT_BACON_TOGGLED'
})

export type ActionGetOperationsRepeat = {
    type: 'ECOMM_GET_OPERATIONS_REPEAT',
    operationsRepeat: Array<OperationDevice>
}

const actionGetOperationsRepeat = (
    operationsRepeat: Array<OperationDevice>
): ActionGetOperationsRepeat => ({
    type: 'ECOMM_GET_OPERATIONS_REPEAT',
    operationsRepeat
})

const mapPickUpTypeToClickAndCollectCatalogName = (
    pickUpType: string
): string | typeof undefined => {
    switch (pickUpType) {
        case TAKEAWAY:
            return 'pick-up'
        case ONSITE:
        case TABLESERVICE:
            return 'on-site'
        case DRIVE:
            return 'drive'
        case PARKING:
            return 'parking'
        case TABLE_ORDER:
            return 'table-order'
    }
}

export const mapPickUpTypeToOrderType = (
    pickUpType: string
): string | typeof undefined => {
    switch (pickUpType) {
        case TABLE_ORDER:
            return 'TABLE_ORDER'

        case orderPickUpType.DELIVERY_IN_APP:
            return 'DELIVERY_IN_APP'

        case TAKEAWAY:
        case DRIVE:
        case TABLESERVICE:
        case ONSITE:
        case PARKING:
            return 'CLICK_AND_COLLECT'
    }
}

const fetchCatalog = (restaurantId: string) => async (
    dispatch: Dispatch<any>,
    getState: GetState
) => {
    try {
        const { ecommerceMode, collectMode } = getState().ecommerceOrderState
        const endpoint =
            ecommerceMode === DELIVERY_IN_APP
                ? endpoints.ecommerce.catalog({
                      isDelivery: true,
                      restaurantId
                  })
                : endpoints.ecommerce.catalog({
                      restaurantId,
                      isDelivery: false,
                      catalogName: mapPickUpTypeToClickAndCollectCatalogName(
                          collectMode || TAKEAWAY
                      )
                  })
        const catalog = await storageHttpClient.get(endpoint)

        dispatch(actionCatalogFetched(catalog.data))

        dispatch(fetchAppSettings())
    } catch (error) {
        if (error.response) {
            throw errors.ECOMM_CATALOG_NOT_FOUND
        }
        if (error.request) {
            throw errors.NETWORK
        }
        throw error
    }
}

const fetchPrivilegesData = () =>
    fetchPageData(
        endpoints.kingdom.privileges.page,
        'PAGE_DATA_PRIVILEGES_RESPONSE',
        true
    )

const fetchPointsData = () =>
    fetchPageData(
        endpoints.kingdom.points.page,
        'PAGE_DATA_POINTS_RESPONSE',
        true
    )

const fetchProfileData = () =>
    fetchPageData(
        endpoints.kingdom.profile.user,
        'PAGE_DATA_PROFILE_RESPONSE',
        true
    )

const getItemsFromIds = (listIds: Array<string>, type: string) => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        const state = getState().ecommerceCatalogState
        let listItems = []
        if (type === catalogType.MENU) {
            listIds.forEach(id => {
                const menu = getMenuById(state, id)
                menu && listItems.push(menu)
            })
        } else {
            listIds.forEach(id => {
                const product = getProductById(state, id)
                product && listItems.push(product)
            })
        }
        return listItems
    }
}

const getOperationsRepeat = (
    deviceCheckPayload: DeviceCheck,
    origin?: string,
    frNumber?: string
) => async (dispatch: Dispatch<any>) => {
    try {
        const url = new URL(endpoints.pages.operation.all)

        if (origin) {
            url.searchParams.append('origin', origin)
        }

        if (frNumber) {
            url.searchParams.append('frNumber', frNumber)
        }

        const operations = await ws.post(url.toString(), deviceCheckPayload)
        dispatch(
            actionGetOperationsRepeat(
                operations.data.map(item => {
                    return {
                        ...item,
                        coupons: item.coupons.map(coupon => {
                            return {
                                ...coupon,
                                promotionId: coupon.promotionSelected
                            }
                        })
                    }
                })
            )
        )
    } catch (error) {
        console.error('error fetching operation repeat', error)
    }
}

export {
    fetchCatalog,
    selectAllergens,
    toggleWithoutBacon,
    getItemsFromIds,
    getOperationsRepeat,
    fetchPointsData,
    fetchPrivilegesData,
    fetchProfileData
}

export type Action =
    | ActionCatalogFetched
    | ActionAllergensSelected
    | ActionWithoutBaconToggled
    | ActionGetOperationsRepeat
    | ActionOperationRepeatDriveCode
    | ActionOperationRepeatSwapCode
